import {Component, Inject, OnInit, ViewEncapsulation, ElementRef, OnDestroy} from '@angular/core';
import { DialogPanelComponentContent } from '@toro/common/shared/models/dialogs/DialogPanelComponentContent';
import { DOCUMENT } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {STOCK_LOGO_PLACEHOLDER} from '@toro/common/shared/resources/consts';
import {OrderMarketTypeEnum} from '@toro/common/shared/enum/orders/market-type.enum';
import {CheckoutService} from '../../../services/checkout.service';
import {RolesUser} from '@toro/common/shared/enum/roles-user.enum';
import {SuitabilityProfilesEnum} from '@toro/common/shared/enum/suitability/suitability-profiles.enum';
import {OrderSideEnum} from '@toro/common/shared/enum/orders/side.enum';
import {Roles} from '@toro/common/shared/models/roles/roles.enum';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import {DialogsService} from '@toro/common/shared/services/dialogs.service';
import {CustodyClientOrder} from '@toro/common/shared/models/custody/custody-client-order.model';
import {Strategy} from '@toro/common/shared/models/exchange/strategy.model';
import {Order} from '@toro/common/shared/models/exchange/order.model';
import {OrderType} from '@toro/common/shared/enum/orders/order-type.enum';
import {OrderStatus} from '@toro/common/shared/enum/orders/order-status.enum';
import {StockCheckoutType} from '@toro/common/shared/models/stocks-exchange/stock-checkout.model';
import {TrackOriginEnum} from '@toro/common/shared/enum/segment/segment-origin.enum';
import {StocksExchangeCheckoutAction} from '../../../state-management/stocks-exchange-checkout/stocks-exchange-checkout.action';
import {CheckoutWrapperComponent} from '../checkout/wrapper/checkout-wrapper.component';
import {BrokerageModel} from '@toro/common/shared/enum/billing/brokerage-model.enum';

@Component({
  selector: 'analysis-preview-dialog',
  templateUrl: './analysis-preview-dialog.component.html',
  styleUrls: ['./analysis-preview-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AnalysisPreviewDialogComponent extends DialogPanelComponentContent implements OnInit, OnDestroy {

  public subscriptions = new Array<Subscription>();
  public symbolID: string;
  public stock: any;
  public companyAnalysis: string;
  public companyTrend: number;
  public isLogged: boolean = false;
  public userRole: number = null;
  public suitabilityProfile: any = null;
  public clientInvestments: CustodyClientOrder[] = [];
  public strategiesInvestments: any[] = [];
  public waiting: boolean = false;
  public isFractionary: boolean = false;
  private confirmSuitabilitySubscription: Subscription;
  private confirmAlredyHaveInvestmentsSubscription: Subscription;
  private confirmAlredyHaveInvestmentsBothSubscription: Subscription;
  private usingZeroModel: boolean = false;

  public constructor(
      @Inject(MAT_DIALOG_DATA) public data,
      @Inject(DOCUMENT) private document: Document,
      public elementRef: ElementRef,
      public settingsService: SettingsService,
      private dialogReference: MatDialogRef<AnalysisPreviewDialogComponent>,
      private store: Store<AppState>,
      private activatedRoute: ActivatedRoute,
      private router: Router,
      private checkoutService: CheckoutService,
      protected dialogsServices: DialogsService,
  ) {
    super(elementRef, true);
    this.document.body.classList.add('freeze');
    this.stock = {variation: 0};

    this.subscriptions.push(this.store.select((s) => s.authentication.currentUser)
        .filter((user) => user)
        .map((user) => user.role)
        .distinctUntilChanged()
        .subscribe((role) => {
          this.isLogged = true;
        }),
    this.store.select((s) => s.userProfile.user)
        .filter((user: any) => user && user.profile ? true : false)
        .subscribe((user) => {
          this.userRole = user.profile;
        }),
        this.store.select((s) => s.stocksExchange.wallet.validStrategies)
            .filter((strategies) => strategies ? true : false)
            .do((strategies) => this.waiting = true)
            .map((strategies) => this.filterUserStrategies(strategies))
            .do((strategies) => this.waiting = false)
            .filter((strategies) => strategies.length ? true : false)
            .subscribe((strategies) => {
              this.strategiesInvestments = strategies;
            }));
  }

  public get isFuture() {
    return this.stock.typeMarket === OrderMarketTypeEnum.Future;
  }

  public get isOption() {
    return this.stock.typeMarket === OrderMarketTypeEnum.Option;
  }

  public ngOnInit() {
    this.isFractionary = this.isFractionaryCondition(this.symbolID, this.stock);
    this.subscriptions.push(this.store.select((s) => s.suitability.profile)
        .filter((f) => f ? true : false)
        .subscribe((data) => {
          this.suitabilityProfile = data;
        }));

    this.activatedRoute.queryParams.subscribe((params) => {
      this.symbolID = params.ativo;
      this.subscriptions.push(
          this.store.select((state) => state.stocksExchange.stocksMarket.instruments)
              .pipe(
                  filter((instrument) => (instrument !== null && instrument[this.symbolID] ? true : false)),
                  map((instrument) => instrument[this.symbolID]))
              .subscribe((stock) => { this.stock = stock; }),

          this.checkoutService.getQuoteStoreData(this.symbolID)
              .subscribe((quote) => {
                if (quote) {
                  this.stock.variation = quote.Variation;
                  this.stock.priceLastTrade =
                      quote.PriceLastTrade          ||
                      quote.OpeningPrice            ||
                      quote.ClosingPricePreviousDay ||
                      quote.priceLastTrade          ||
                      quote.openingPrice            ||
                      quote.closingPricePreviousDay;
                }
              }),
          this.store.select((state) => state.billing)
              .subscribe((billing: any) => {
                if (billing && billing.brokerageModel.type) {
                  this.usingZeroModel = (billing.brokerageModel.type === BrokerageModel.Zero);
                }
              }),
          this.store.select((state) => state.trends)
              .subscribe((trendData: any) => {
                this.companyAnalysis = trendData.companyTrend.about;
                this.companyTrend    = trendData.trend ? trendData.trend.trend : 3; // caso não existir, atribuir como tendencia indefinida
          }),
        );
    });
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.router.navigate([], {queryParams: {}});
  }

  /**
   * Metodo para comprar uma ação
   */
  public buy() {
    const isIdentified = this.userRole === RolesUser.Identified;
    if (!this.isLogged || isIdentified) return this.goToRegister();
    if (!this.suitabilityProfile) return;
    const isConservative = this.suitabilityProfile.profile === SuitabilityProfilesEnum.Conservative;
    if (isConservative) {
      this.confirmSuitabilityProfile();
    } else {
      const userHasInvestments = this.checkIfUserHaveInvestments();
      if (userHasInvestments.sell && userHasInvestments.buy) {
        this.confirmAlredyHaveInvestmentsBothSides(OrderSideEnum.Compra);
      } else if (userHasInvestments.sell && !userHasInvestments.buy) { // sell
        const options = [
          { id: 'manage', value: 'manage', text: 'Gerenciar meus investimentos' },
          { id: 'new', value: 'new', text: 'Criar um novo investimento' },
        ];
        const title = `Você possui ${this.symbolID} vendid${this.isFuture ? 'os' : 'as'}`;
        const content = `Você clicou em comprar mas já possui um investimento 'vendido' de ${this.symbolID}. O que deseja fazer?`;
        this.confirmAlredyHaveInvestments(options, title, content, true);
      } else {
        this.goToCheckoutDirect(true, this.isOption);
      }
    }
  }

  /**
   * Metodo para vender uma ação
   */
  public sell() {
    const isIdentified = this.userRole === RolesUser.Identified;
    if (!this.isLogged || isIdentified) return this.goToRegister();
    if (!this.suitabilityProfile) return;
    const isConservative = this.suitabilityProfile.profile === SuitabilityProfilesEnum.Conservative;
    if (isConservative) {
      return this.confirmSuitabilityProfile();
    } else {
      const userHasInvestments = this.checkIfUserHaveInvestments();
      if (this.isOption && userHasInvestments.buy) {
        return this.dialogsServices.confirmAction(
            `Você possui ${this.symbolID} compradas`,
            'Acesse a área de Meus Investimentos para vender as opções que já possui.',
            'Acessar Meus Investimentos',
            'Cancelar',
        )
            .subscribe((data) => {
              if (data) {
                this.router.navigateByUrl(`/meus-investimentos`);
              }
            });
      } else if (this.isOption && !userHasInvestments.buy) {
        return this.dialogsServices.showSimpleModal(
            'icon_alert',
            'Você não possui nenhuma opção para vender.',
            'No caso de opções, você não pode entrar vendido no mercado.',
            'Continue');
      } else if (userHasInvestments.sell && userHasInvestments.buy) {
        return this.confirmAlredyHaveInvestmentsBothSides(OrderSideEnum.Venda);
      } else if (!userHasInvestments.sell && userHasInvestments.buy) {
        const options = [
          { id: 'manage', value: 'manage', text: `Gerenciar meus investimentos` },
          { id: 'new', value: 'new', text: 'Criar um novo investimento' },
        ];
        const title = `Você possui ${this.symbolID} comprad${this.isFuture ? 'os' : 'as'}`;
        // tslint:disable-next-line:max-line-length
        const content = `Você clicou em vender mas já possui ${this.isFuture ? 'contratos' : 'ações'} de ${this.symbolID}. O que deseja fazer?`;
        return this.confirmAlredyHaveInvestments(options, title, content, false);
      } else {
        return this.goToCheckoutDirect(false, this.isOption);
      }
    }
  }

  public closeDialog() {
    this.document.body.classList.remove('freeze');
    this.dialogReference.close(null);
    this.dialogReference = null;
  }

  public goToAnalysis() {
    this.router.navigate([`/analise/${this.symbolID}`]);
  }

  public noLogoHandler(event) {
    this.stock.logo.content = STOCK_LOGO_PLACEHOLDER;
  }

  private goToRegister(): void {
    window.open(this.settingsService.getConfigurationKeyValue('rau'), '_self');
  }

  private confirmSuitabilityProfile(): Subscription {
    this.confirmSuitabilitySubscription = this.dialogsServices.confirmAction(
        'Você não possui perfil para essa operação',
        'Você não possui perfil adequado para essa operação, o que deseja fazer ?',
        'Refazer Perfil',
        'Cancelar',
    )
        .subscribe((data) => {
          if (data) {
            this.router.navigateByUrl('/perfil');
          }
        });

    return this.confirmSuitabilitySubscription;
  }

  private filterUserStrategies(investments: Strategy[]): any[] {

    const investmentsArray: Strategy[] = Object.keys(investments)
        .map((strategy) => investments[strategy]);

    const investmentsWithoutExecutedLeg: any = investmentsArray.map((strategy) => {
      const legs: Order[] = strategy.legs;
      const inputLeg = legs.find((leg) => leg.internalOrderType === OrderType.Input);
      const stopGainLeg = legs.find((leg) => leg.internalOrderType === OrderType.StopGain);
      const stopLossLeg = legs.find((leg) => leg.internalOrderType === OrderType.StopLoss);

      if (!inputLeg || !stopGainLeg || !stopLossLeg) return false;

      if (inputLeg.status === OrderStatus.Filled || inputLeg.status === OrderStatus.PartiallyFilled) {
        if (stopGainLeg.status === OrderStatus.PendingTrigger && stopLossLeg.status === OrderStatus.PendingTrigger) {
          return { ...inputLeg, ...{ symbol: strategy.symbol } };
        } else {
          return null;
        }
      } else {
        return null;
      }

    })
        .filter((strategy) => strategy);
    return investmentsWithoutExecutedLeg;
  }

  private confirmAlredyHaveInvestmentsBothSides(sideTrigger: OrderSideEnum): Subscription {
    const options = [
      { id: 'new', value: 'new', text: 'Criar um novo investimento' },
      { id: 'manage', value: 'manage', text: 'Gerenciar meus investimentos' },
    ];
    this.confirmAlredyHaveInvestmentsBothSubscription = this.dialogsServices.confirmActionWithOptions(
        `Você possui ${this.symbolID} ${this.isFuture ? 'comprados e vendidos' : 'compradas e vendidas'}`,
        // tslint:disable-next-line:max-line-length
        `Você clicou em ${sideTrigger === OrderSideEnum.Compra ? 'comprar' : 'vender'} mas já possui ${this.isFuture ? 'contratos' : 'ações'} de ${this.symbolID}. O que deseja fazer?`,
        options,
        'Cancelar',
        'Confirmar',
    )
        .subscribe((data) => {
          if (data) {
            if (data === 'manage') {
              this.router.navigateByUrl('/meus-investimentos');
            } else if (data === 'new') {
              this.goToCheckoutDirect(sideTrigger === OrderSideEnum.Compra, false);
            }
          }
        });

    return this.confirmAlredyHaveInvestmentsBothSubscription;
  }

  private goToCheckoutDirect(isPurchase: boolean, isOption: boolean) {
    const item = new StockCheckoutType();
    item.symbol = this.symbolID;
    item.isFuture = this.isFuture;
    item.purchase = isPurchase;
    item.origin = TrackOriginEnum.EXPLORE_MARKET;
    this.store.dispatch({ type: StocksExchangeCheckoutAction.setStockCheckout, payload: item });

    if (this.usingZeroModel) {
      this.dialogsServices.showComponentAsAsidePanel(CheckoutWrapperComponent, true);
    } else {
      const url = this.isFractionary || isOption ? '/checkout' : '/checkout/planejado';
      this.router.navigateByUrl(url);
    }
  }

  private isFractionaryCondition(symbol: string, details: any): boolean {
    if (symbol === undefined) return false;

    return (this.symbolID.endsWith('F') || details.typeMarket === OrderMarketTypeEnum.Fractionary) ||
        (details.typeMarket === OrderMarketTypeEnum.ETFFractionary && this.symbolID.endsWith('F'));
  }

  private confirmAlredyHaveInvestments(options: any[], title: string, content: string, isPurchase: boolean): Subscription {
    this.confirmAlredyHaveInvestmentsSubscription = this.dialogsServices.confirmActionWithOptions(
        title,
        content,
        options,
        'Cancelar',
        'Confirmar',
    )
        .subscribe((data) => {
          if (data) {
            if (data === 'manage') {
              this.router.navigateByUrl('/meus-investimentos');
            } else if (data === 'new') {
              this.goToCheckoutDirect(isPurchase, false);
            }
          }
        });

    return this.confirmAlredyHaveInvestmentsSubscription;
  }

  private checkIfUserHaveInvestments(): any {
    return {
      buy: (this.clientInvestments.some((order) => order.total > 0 && order.symbol === this.symbolID) ||
          this.strategiesInvestments.some((strategy) => strategy.side === OrderSideEnum.Compra && strategy.symbol === this.symbolID)),
      sell: (this.clientInvestments.some((order) => order.total < 0 && order.symbol === this.symbolID) ||
          this.strategiesInvestments.some((strategy) => strategy.side === OrderSideEnum.Venda && strategy.symbol === this.symbolID)),
    };
  }
}
